import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  Optional,
} from '@angular/core';
import { asyncScheduler, Observable, Subscription } from 'rxjs';
import { observeOn, tap } from 'rxjs/operators';
import { FilterModel, GetRowIdFunc, IServerSideGetRowsParams } from 'ag-grid-community';
import {
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  GridReadyEvent,
  PasteEndEvent,
  PasteStartEvent,
  RowEditingStartedEvent,
  RowEditingStoppedEvent,
  GridApi,
} from 'ag-grid-community';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatSelectChange } from '@angular/material/select';
import { TRANSLATOR, Translator } from '@miq/utils';
import { InputType } from '@miq/catalog';
import { AdvancedInputFactory } from '@controls/advanced-controls/input-control/pipes/to-advanced-config.helper';
import { DataObject } from '@shared/models/data-object.model';
import { comparedTwoValuesByValueType, standardComparator } from '@shared/services/sorting.service';
import { IOption } from '@shared/models/icommons.model';
import {
  AgCallback,
  AgGridOptions,
  AgGridOptionsDomLayout,
  AgGridOptionsEditType,
  AgRowModelType,
  AgStreamParams,
  AgStreamResult,
  MiqAgCellEditors,
  MiqAgCellRenderers,
  MiqAgColumn,
  RowGroupingDisplay,
} from './ag-table-grid-options';
import { AGTABLE_EVENT_COMMANDS_PROVIDER, AgTableEventCommandsProvider } from './change-event-command';
import { ChangeEventCommandServiceFactory } from './change-event-command.service';
import { InputTypeToCellEditorMap } from './inputs/ag-cell-renderer-map';

const defaultOptions = {
  sortable: false,
  resizable: false,
  filter: false,
  suppressHeaderMenuButton: true,
};

const defaultGridOptions: Partial<AgGridOptions> = {
  pagination: false,
  editType: AgGridOptionsEditType.OneAtATime,
  autoSizeAll: true,
  paginationPageSize: 0,
};

/**
 * Market IQ Table Component
 *
 * A composable, highly-typed wrapper for ag-grid supporting both client-side and server-side row models.
 *
 * @remarks
 * - Only passes rowData to ag-grid when not using server-side row model.
 * - Designed for strict type safety and composability.
 */
@UntilDestroy()
@Component({
  selector: 'miq-ag-table',
  templateUrl: './ag-table.component.html',
  styleUrls: ['./ag-table.component.scss'],
  exportAs: 'miqAgTable',
  providers: [ChangeEventCommandServiceFactory],
  standalone: false,
})
export class AgTableComponent implements OnInit, OnChanges {
  /**
   * The data source for the table. Should be an array of records for client-side row model,
   * or an empty array/null for server-side row model.
   */
  @Input() dataSource: any[] = [];
  @Input() columns: MiqAgColumn[] = [];
  @Input() columnsWithCommands: MiqAgColumn[];

  @Input() options: Record<string, any>;
  @Input() gridOptions: Partial<AgGridOptions>;
  @Input() customComponents: Record<string, any> = {};
  @Input() callBacks: { [key: string]: AgCallback } = {};
  @Input() getRowId: GetRowIdFunc;

  @Output() gridReady = new EventEmitter();
  @Output() firstDataRendered = new EventEmitter();
  @Output() rowValueChanged = new EventEmitter();
  @Output() columnMoved = new EventEmitter();
  @Output() cellValueChanged = new EventEmitter();
  @Output() cellEditingStopped = new EventEmitter<CellEditingStoppedEvent>();
  @Output() rowEditingStarted = new EventEmitter<RowEditingStartedEvent>();
  @Output() rowEditingStopped = new EventEmitter<RowEditingStoppedEvent>();
  @Output() rowClicked = new EventEmitter();
  @Output() gridColumnsChanged = new EventEmitter();
  @Output() filterChanged = new EventEmitter();
  @Output() sortChanged = new EventEmitter();
  @Output() rowSelected = new EventEmitter();
  @Output() cellEditingStarted = new EventEmitter();
  @Output() rowGroupOpened = new EventEmitter();
  @Output() rowDragEnd = new EventEmitter();
  @Output() rowDragMove = new EventEmitter();
  @Output() rowDragEnter = new EventEmitter();
  @Output() paginationChanged = new EventEmitter();
  @Output() paginationValueChanged = new EventEmitter();
  @Output() modelUpdated = new EventEmitter();

  public isAutoHeight = false;

  /**
   * The options for the pagination page size
   */
  public paginationPageSizeOptions: IOption[];

  // private columnGridApi: ColumnApi;
  // // Deprecated in v32 - functionality merged into gridApi
  private gridApi: GridApi;
  private areAllValuesDisplayed: boolean;

  frameworkComponents: Record<string, unknown>;

  cellValueChangeSubs: Subscription = null;

  constructor(
    @Inject(TRANSLATOR) public translationService: Translator,
    private handlerFactory: ChangeEventCommandServiceFactory,
    @Optional() @Inject(AGTABLE_EVENT_COMMANDS_PROVIDER) private eventCommandsProvider?: AgTableEventCommandsProvider
  ) {}

  /**
   * The row data for ag-grid, or null for server-side row model.
   * Set in ngOnInit and ngOnChanges for OnPush compatibility.
   */
  public rowDataForModel: ReadonlyArray<Record<string, unknown>> | null = null;

  ngOnInit() {
    this.initFramework();
    this.processOptions();
    this.processGridOptions();
    this.processColumns();
    this.setRowDataForModel();
  }

  ngOnChanges({ columns, dataSource, gridOptions }: SimpleChanges) {
    if (columns && columns.currentValue) {
      this.processColumns();
    }
    if (this.gridApi && dataSource?.currentValue && !!this.areAllValuesDisplayed) {
      this.gridApi.updateGridOptions({ paginationPageSize: dataSource.currentValue.length });
    }
    if (gridOptions && gridOptions.currentValue) {
      this.processGridOptions();
    }
    this.setRowDataForModel();
  }

  /**
   * Sets the rowDataForModel property based on the current row model type.
   * Ensures rowData is not passed for server-side row model.
   */
  private setRowDataForModel(): void {
    this.rowDataForModel = this.gridOptions?.rowModelType === AgRowModelType.ServerSide ? null : this.dataSource;
  }

  public pageSizeChanged($event: MatSelectChange) {
    // when $event.value is not a number is treated as "All" so we set page size as data source length (MIQ-25816)
    this.areAllValuesDisplayed = isNaN($event?.value);
    const pageSize = this.areAllValuesDisplayed ? this.dataSource.length : Number($event.value);
    this.gridApi.updateGridOptions({ paginationPageSize: pageSize });
    const value = this.paginationPageSizeOptions.find(option => option.key === $event?.value)?.value;
    this.paginationValueChanged.emit(value);
  }

  private checkRowModel() {
    // If the row model type is set to server, checks if dataStream is set, otherwise changes the row type to client
    if (this.gridOptions.rowModelType === AgRowModelType.ServerSide && this.gridOptions.dataStream == null) {
      this.gridOptions.rowModelType = AgRowModelType.ClientSide;
    }
  }

  private initFramework() {
    this.frameworkComponents = {
      // renderers
      ...MiqAgCellRenderers,
      // editors
      ...MiqAgCellEditors,
      // custom to enable plugins of external OR specialized components
      ...this.customComponents,
    };
  }

  private processColumns() {
    if (this.options && this.gridOptions && this.columns) {
      this.columns = this.configureColumns(this.columns);
      this._connectCommands();
    }
  }

  private _connectCommands() {
    let columnsWithCommands = [];
    if (this.cellValueChangeSubs != null) {
      this.cellValueChangeSubs.unsubscribe();
    }

    columnsWithCommands = this.columns;

    if (this.eventCommandsProvider != null) {
      columnsWithCommands = this.eventCommandsProvider.getColumnsCommands();
    }

    columnsWithCommands.flatMap(column => {
      if (column.children && column.children.length > 0) {
        return column.children.filter(childColumn => childColumn.changeEventCommand);
      } else if (column.changeEventCommand) {
        return [column];
      }
      return [];
    });

    this.cellValueChangeSubs = this.cellValueChanged
      .pipe(
        untilDestroyed(this),
        tap(event =>
          columnsWithCommands
            .filter(
              c =>
                this._hasColumnEventCommand(c, event.colDef.columnId?.toString()) ||
                (c.unit != null && this._hasColumnEventCommand(c.unit, event.colDef.columnId?.toString()))
            )
            .forEach(c => {
              if (this._hasColumnEventCommand(c, event.colDef.columnId?.toString())) {
                this.handleEventCommand(event, c);
              }
              if (c.unit != null && this._hasColumnEventCommand(c.unit, event.colDef.columnId?.toString())) {
                this.handleEventCommand(event, c.unit);
              }
            })
        )
      )
      .subscribe();
  }

  private _hasColumnEventCommand = (c: MiqAgColumn, targetColumnId: string) => {
    return c.changeEventCommand != null && c.changeEventCommand?.events?.some(id => id.toString() === targetColumnId);
  };

  private handleEventCommand(event: CellValueChangedEvent, column: MiqAgColumn) {
    const { changeEventCommand } = column;
    const handler = this.handlerFactory.getHandler(changeEventCommand.handler);
    if (handler) {
      handler.handle(event, column);
    }
  }

  private processOptions() {
    this.options = { ...defaultOptions, ...this.options };
  }

  private processGridOptions() {
    this.gridOptions = { ...defaultGridOptions, ...this.gridOptions };

    this.gridOptions.suppressKeyboardEvent = this.gridOptions.suppressKeyboardEvent || (() => false);
    if (this.gridOptions.groupDisplayType == null && !this.gridOptions.treeData) {
      this.gridOptions.groupDisplayType = this.gridOptions.groupSuppressAutoColumn
        ? RowGroupingDisplay.SingleColumn
        : RowGroupingDisplay.MultipleColumns;
    }
    this.gridOptions.noRowsToShow = this.translationService.translate('shared.noRowsToShow');
    // This value is used to identify that the table is in auto height and remove the style.height param in html
    this.isAutoHeight =
      !!this.gridOptions.domLayout && this.gridOptions.domLayout === AgGridOptionsDomLayout.AutoHeight;
    this.areAllValuesDisplayed = isNaN(this.gridOptions?.paginationPageSize as number);

    // init pagination options, always convert numbers to IOption if they are not already in that format
    this.paginationPageSizeOptions = this.gridOptions.paginationPageSizeOptions as IOption[];
    if (this.gridOptions?.paginationPageSizeOptions?.every?.(Number.isInteger)) {
      this.paginationPageSizeOptions = this.gridOptions.paginationPageSizeOptions.map(key => ({
        key,
        value: `${key}`,
      }));
    }

    this.checkRowModel();
  }

  private configureColumns(columns: MiqAgColumn[]) {
    return columns.map(({ tooltip, children, ...column }) => {
      return children
        ? { ...column, children: this.configureColumns(children) }
        : Object.assign(
            column,
            this.mapRenderers(column),
            this.mapTooltip(tooltip as string | AgCallback),
            this.mapEditors(column),
            this.processMenuTab(column),
            this.mapSortingMethod(column)
          );
    });
  }

  private mapRenderers(column: MiqAgColumn) {
    const userRenderer = column.cellRenderer;
    return userRenderer ? { cellRenderer: userRenderer } : {};
  }

  private processMenuTab(column: MiqAgColumn) {
    return {
      menuTabs: column.menuTabs || [],
    };
  }

  private mapTooltip(tooltip: string | AgCallback) {
    let result = {};
    // ag-grid accepts tooltip as fn, so we need to ensure it is a fn
    if (tooltip) {
      const tooltipFn = typeof tooltip !== 'function' ? () => tooltip : tooltip;
      result = {
        tooltipValueGetter: tooltipFn,
      };
    }
    return result;
  }

  private mapEditors(column: MiqAgColumn): DataObject {
    const inputType = this._getInputType(column);
    let cellEditor: string;
    if (column.customCellEditor) {
      cellEditor = column.customCellEditor;
    } else if (column.unit && inputType === InputType.Number) {
      cellEditor = 'agUomComponent';
    } else {
      cellEditor = InputTypeToCellEditorMap.get(inputType);
    }
    return cellEditor == null ? {} : this._makeCellEditorConfig(cellEditor, column);
  }

  /**
   * Derives what input type to use
   * @param column
   * @returns
   */
  private _getInputType(column: MiqAgColumn): InputType {
    const { dataType, inputType, lookupCategory } = column;
    // derive based on input & data type as controls module does
    const params = { dataType, inputType: inputType as InputType, lookupCategory };
    // based on combination of input and data type
    return AdvancedInputFactory.getInputType(params);
  }

  /**
   * Builds and returns an object with cellEditor name and params
   * @param cellEditor
   * @param column
   * @returns
   */
  private _makeCellEditorConfig(cellEditor: string, column: MiqAgColumn) {
    return {
      cellEditor,
      cellEditorParams: { ...column, ...column.cellEditorParams, gridOptions: this.gridOptions },
    };
  }

  /**
   * Provides comparator config to sort the column by
   * @returns Object with comparator attribute
   */
  private mapSortingMethod({ comparator }: MiqAgColumn) {
    if (comparator) {
      return { comparator };
    }
    /**
     * According to ag-grid docs:
     * 0 valueA is the same as valueB
     * > 0 Sortf valueA after valueB
     * < 0 Sort valueA before valueB
     */
    return {
      comparator: (leftValue, rightValue) => {
        const leftSingleValue = Array.isArray(leftValue)
          ? leftValue.sort((a, b) => String(a).localeCompare(String(b))).join('')
          : leftValue;
        const rightSingleValue = Array.isArray(rightValue)
          ? rightValue.sort((a, b) => String(a).localeCompare(String(b))).join('')
          : rightValue;
        if (typeof leftSingleValue === 'string' || typeof rightSingleValue === 'string') {
          return this._assignComparatorByValueType(leftSingleValue, rightSingleValue);
        }
        return standardComparator(leftSingleValue || '', rightSingleValue || '');
      },
    };
  }

  /**
   *  Compare two values and returns:
   *  a Negative number if the first value is Before the second
   *  a Positive number if the first value is After the second
   * @param leftSingleValue
   * @param rightSingleValue
   * @returns number
   */
  private _assignComparatorByValueType(leftSingleValue, rightSingleValue) {
    // The possible values for a number field could be: *****, numbers, null
    return comparedTwoValuesByValueType(leftSingleValue, rightSingleValue);
  }

  private createDataSource(functionStream: (tableParams: AgStreamParams) => Observable<AgStreamResult>) {
    return {
      getRows: function getTableRows(params: IServerSideGetRowsParams) {
        const {
          request: { startRow, endRow, sortModel, filterModel },
        } = params;
        // Use AgStreamParams type for request
        const request: AgStreamParams = { startRow, endRow, sort: sortModel, filters: filterModel as FilterModel };
        functionStream(request)
          .pipe(observeOn(asyncScheduler, 0))
          .subscribe(
            (result: AgStreamResult) => {
              params.success({
                rowData: result.data,
                rowCount: result.totalRows,
              });
            },
            () => {
              params.fail();
            }
          );
      },
    };
  }

  private bindDataStream({ api }: { api: GridApi }) {
    const isServerSide =
      this.gridOptions.rowModelType === AgRowModelType.ServerSide && this.gridOptions.dataStream != null;
    if (isServerSide) {
      // TODO (AG-Grid): setting the data source when the model is server side is causing the warning:
      //  AG Grid: rowData is not supported with the 'serverSide' row model
      const dataSource = this.createDataSource(this.gridOptions.dataStream);
      // AG Grid v31+: use setGridOption instead of setServerSideDatasource
      if (typeof api.setGridOption === 'function') {
        api.setGridOption('serverSideDatasource', dataSource);
        return;
      }
      // Use the correct method for server-side datasource in v32
      api.setGridOption('serverSideDatasource', dataSource);
    }
  }

  onGridReady($event: GridReadyEvent) {
    this.gridApi = $event.api;
    // columnApi functionality is now merged into api in v32
    // TODO: Check that the following lines of code are necessary and working properly
    // AG Grid v31+: defaultColDef and animateRows must be set as component inputs, not in gridOptions
    // Set them on the grid instance if needed
    /*     const gridOptions = this.gridOptions as Partial<AgGridOptions>;
    if (this.gridApi && gridOptions.defaultColDef) {
      this.gridApi.setGridOption('defaultColDef', gridOptions.defaultColDef);
    }
    if (this.gridApi && typeof gridOptions.animateRows === 'boolean') {
      this.gridApi.setGridOption('animateRows', gridOptions.animateRows);
    } */
    if (this.gridOptions.autoSizeAll) {
      setTimeout(() => this.autoSizeAll());
    }
    this.bindDataStream($event);
    this.gridReady.emit($event);
  }

  private autoSizeAll() {
    this.gridApi.autoSizeAllColumns(false);
  }

  pasteStarted(event: PasteStartEvent) {
    this.#proxyCallback(this.gridOptions.onPasteStart, event);
  }
  pasteEnded(event: PasteEndEvent) {
    this.#proxyCallback(this.gridOptions.onPasteEnd, event);
  }

  #proxyCallback = (cb: AgCallback, proxyEvent: PasteStartEvent | PasteEndEvent) => {
    if (typeof cb === 'function') cb(proxyEvent);
  };
}
